import React from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import PageLoader from 'components/LoadingButton/PageLoader';
import SidebarMenu from 'components/Sidebar/Sidebar';
import RouterErrorPage from 'pages/RouterErrorPage';
import FullscreenNotification from 'components/Header/FullscreenNotification';
import useGetLoggedInUser from 'services/hooks/LoggedInUser/useGetLoggedInUser';
import LazyRouteWrapper from 'components/LazyRouteWrapper';
import ProtectedRoute from 'components/ProtectedRoute';
import OnboardingStepGuard from 'components/OnboardingStepGuard';
import OnboardingCompleteGuard from 'components/OnboardingCompleteGuard';
import { RedirectToOrderRequest } from 'components/RedirectToHome';

// Import all lazy-loaded routes 
import * as LazyRoutes from 'utils/lazyRoutes';
import * as MarketplaceRoutes from 'utils/lazyRoutes.marketplace';
import * as BuyingRoutes from 'utils/lazyRoutes.buying';

/** Root URL has no standalone page: send guests to login, signed-in users to dashboard home. */
function RootRedirect() {
  const { data: userData, isLoading } = useGetLoggedInUser();
  if (isLoading) return <PageLoader />;
  const body = userData?.data;
  const isLoggedIn = body && body !== false && body !== null && body !== '';
  if (isLoggedIn) return <Navigate to="/home" replace />;
  return <Navigate to="/login" replace />;
}

// Dashboard routes - protected app lives under /home (root / redirects to login or /home)
const dashboardChildren = [
      {
        index: true,
        element: (
          <LazyRouteWrapper>
            <MarketplaceRoutes.MarketPlace />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'bulk-purchase',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.BulkPurchasePage />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'bulk-purchase/list',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.BulkPurchaseListPage />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'smart-bulk-purchase',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.SmartBulkPurchasePage />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'hotel-planner',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.HotelPlannerPage />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'hotel-planner/list',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.HotelPlannerPageList />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'search',
        element: (
          <LazyRouteWrapper>
            <MarketplaceRoutes.UnifiedProductListPage />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'brand-products',
        element: (
          <LazyRouteWrapper>
            <MarketplaceRoutes.BrandProductsPage />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'brand-products/:companyId',
        element: (
          <LazyRouteWrapper>
            <MarketplaceRoutes.BrandProductsPage />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'ready-stock',
        element: (
          <LazyRouteWrapper>
            <MarketplaceRoutes.ReadyStockPage />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'htmltopdf',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.HtmlToPdfConverter />
          </LazyRouteWrapper>
        ),
      },
      {
        path: 'planpricing',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.PlanPricingDetails />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'voucherorderlist',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.VoucherOrderList />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'sellervoucherorderlist',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.VoucherOrdeListSeller />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'spacificvoucher/:id',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.VoucherTypeOne />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'membershiprenewalinvoice',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.MembershipRenewalInvoice />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'valueandgiftvoucher/:id',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.VoucherTypeTwo />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'textilesvoucherprev/:id',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.TextilesVoucherPrev />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'purchaseorderdetails/:id',
        element: <RedirectToOrderRequest />,
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'proformainvoice/:id',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.ProformaInvoice />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'mediapurchaseorderdetails/:id',
        element: <RedirectToOrderRequest />,
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'mediapurchaseorderdetailsseller/:id',
        element: <RedirectToOrderRequest />,
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'membershipfeesinvoice',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.MembershipFeesInvoice />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'taxinvoice/:id',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.TaxInvoice />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'buyer-tax-invoice/:orderRequestId',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.BuyerTaxInvoice />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'shippinginvoice/:id',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.ShippingInvoice />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'vouchertaxinvoice/:id',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.VoucherTaxInvoice />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'voucherproformainvoice/:id',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.VoucherProformaInvoice />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'mediataxinvoice/:id',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.MediaTaxInvoice />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'commission/:id',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.Commission />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'bscommission/:id',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.BSCommission />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'commissionByOrderId/:OrderId',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.CommissionByOrderId />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'commissionlist',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.CommissionList />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'bw-tds-debit-note',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.BWTDSDebitNoteList />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'bw-tds-debit-note/:id',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.BWTDSDebitNoteDetail />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'help-and-feedback',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.HelpAndFeedbackPage />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'product',
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'productanalytics',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.ProductAnalysis />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'fullscreennotification',
        element: <FullscreenNotification />,
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'conditions',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.Conditions />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'dashboard',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.MyWallet />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'ai-media-planner',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.AIMediaPlanner />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'wallet',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.MyWallet />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'cart',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.Cart />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'wishlist',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.Wishlist />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'wishlistproductdetail/:id',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.WishlistProductDetails />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'wishlistmediabuying/:id',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.WishlistMediaBuying />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'wishlistvoucherdetail/:id',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.WishlistVoucherBuyingPage />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'invoice/:id',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.Invoice />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'purchaseorder/:id',
        element: <RedirectToOrderRequest />,
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'proformaInvoice/:id',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.ProformaInvoice />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'performainvoice/:id/:orderId',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.PerformaInvoice />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        // Notification links carry only the PI id; the page falls back to the
        // invoice's own OrderSummeryId when :orderId is absent.
        path: 'performainvoice/:id',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.PerformaInvoice />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'sellerperformainvoice/:id',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.SellerProformaInvoiceDetails />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'loader',
        element: <PageLoader />,
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'myorderlist',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.OrderDetailsPage />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'sellerorderlist',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.SellerOrderList />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'trackorders',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.TrackYourOrderPage />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'profile',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.ProfileInterest />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'ordersummerydetails/:id',
        element: <RedirectToOrderRequest />,
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'Voucherordersummerydetailspage/:id',
        element: <Navigate to="/home/order-requests" replace />,
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'mediaordersummerydetails/:id',
        element: <Navigate to="/home/order-requests" replace />,
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'voucherdetail/:id',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.VoucherBuyingPage />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'giftandvaluevoucher/:id',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.GiftAndValueVoucherDetails />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'electronicsdetail/:id',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.ElectronicsDetails />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'mediabuying/:id',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.MediaBuying />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'multiplexmediabuying/:id',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.MultiplexMediaBuying />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'digitalAndHoardingMediaBuying/:id',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.DigitalAndHoardingBuying />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'membershipdetail',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.MembershipDetail />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'productdetail/:id',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.ProductDetails />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'company_members',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.CompanyMembers />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'deleted_members',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.DeletedMembers />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'member_details/:id',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.MemberDetailsPage />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'memberdirectory',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.MemberDirectory />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'add_member',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.AddUserPage />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'allinvoices',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.InvoicePage />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'wallettransactions',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.TransactionsPage />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'physical',
        element: <Navigate to="/home" replace />,
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'unsubscribe',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.Unsubscribe />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'eephysical',
        element: <Navigate to="/home" replace />,
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'officeappreal',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.OfficeAppreal />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'sellerhub',
        element: <Navigate to="/home" replace />,
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'addmember',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.AddMember />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'edit_member/:id',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.EditMember />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'sellerdetailedordersummary/:id',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.SellerDetailedOrderSummary />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'detailedordersummary/:id',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.DetailedOrderSummary />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'order-requests',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.OrderRequestList />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'order-request/:id/choose-transport',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.OrderRequestChooseTransport />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'order-request/:id',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.OrderRequestDetail />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'order-request/:id/payment-return',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.OrderRequestPaymentReturn />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      
      {
        path: 'sellerdetails',
        element: <Navigate to="/home/order-requests" replace />,
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'buyerordersummaryhistory',
        element: <Navigate to="/home/order-requests" replace />,
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'choosetransport/:id',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.ChooseTransportation />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'voucherordersummary/:id',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.VoucherOrderSummary />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'mediapilist',
        element: (
          <LazyRouteWrapper>
            <BuyingRoutes.MediaPIList />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'sellermediapilist',
        element: (
          <LazyRouteWrapper>
            <BuyingRoutes.SellerMediapiList />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'orderdetails/:id/:company_id/:type',
        element: (
          <LazyRouteWrapper>
            <BuyingRoutes.OrderDetails />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'orderdetailsseller/:id',
        element: (
          <LazyRouteWrapper>
            <LazyRoutes.OrderDetailsForSeller />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'vouchers',
        element: <Navigate to="/home" replace />,
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'voucherdesign/:id',
        element: <Navigate to="/home" replace />,
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'marketplace/category/:type',
        element: (
          <LazyRouteWrapper>
            <MarketplaceRoutes.UnifiedcategoryBase />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'marketplaceproductlist',
        element: (
          <LazyRouteWrapper>
            <MarketplaceRoutes.UnifiedProductListPage />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'marketplaceproductlist/:id',
        element: (
          <LazyRouteWrapper>
            <MarketplaceRoutes.UnifiedProductListPage />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'affordable-with-credits/:id',
        element: (
          <LazyRouteWrapper>
            <MarketplaceRoutes.UnifiedProductListPage />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'affordable-with-credits',
        element: (
          <LazyRouteWrapper>
            <MarketplaceRoutes.UnifiedProductListPage />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      {
        path: 'marketplaceproductpreview/:id/:type',
        element: (
          <LazyRouteWrapper>
            <MarketplaceRoutes.UnifiedProductPreview />
          </LazyRouteWrapper>
        ),
        errorElement: <RouterErrorPage />,
      },
      // Catch-all route for unmatched paths within /home
      {
        path: '*',
        element: <RouterErrorPage />,
      },
];

// Dashboard routes - all routes that were under /home, now at root
const dashboardRoutes = [
  {
    path: '/',
    element: <RootRedirect />,
  },
  // Public Explore page - same marketplace landing, buying actions hidden (guest-accessible)
  {
    path: '/explore',
    element: (
      <LazyRouteWrapper>
        <MarketplaceRoutes.MarketPlace />
      </LazyRouteWrapper>
    ),
    errorElement: <RouterErrorPage />,
  },
  // Public barter calculator (guest-accessible): estimates yearly cash savings and shows related listings
  {
    path: '/calculator',
    element: (
      <LazyRouteWrapper>
        <MarketplaceRoutes.BarterCalculator />
      </LazyRouteWrapper>
    ),
    errorElement: <RouterErrorPage />,
  },
  // Home route alias - includes all routes for /home/* paths
  {
    path: '/home',
    element: (
      <ProtectedRoute>
        <OnboardingCompleteGuard>
          <SidebarMenu />
        </OnboardingCompleteGuard>
      </ProtectedRoute>
    ),
    errorElement: <RouterErrorPage />,
    children: dashboardChildren, // Share the same children array
  },
  // Auth routes - redirect to landing for login
  {
    path: '/login',
    element: (
      <LazyRouteWrapper>
        <LazyRoutes.LoginPage />
      </LazyRouteWrapper>
    ),
    errorElement: <RouterErrorPage />,
  },
  {
    path: '/login2fa',
    element: (
      <LazyRouteWrapper>
        <LazyRoutes.Login2FAOtp />
      </LazyRouteWrapper>
    ),
    errorElement: <RouterErrorPage />,
  },
  // Create account flow (same route names as BXI Frontend)
  {
    path: '/createaccount',
    element: (
      <LazyRouteWrapper>
        <LazyRoutes.CreateAccount />
      </LazyRouteWrapper>
    ),
    errorElement: <RouterErrorPage />,
  },
  {
    path: '/otp',
    element: (
      <LazyRouteWrapper>
        <LazyRoutes.OtpPage />
      </LazyRouteWrapper>
    ),
    errorElement: <RouterErrorPage />,
  },
  {
    path: '/addpassword',
    element: (
      <LazyRouteWrapper>
        <LazyRoutes.AddPasswordPage />
      </LazyRouteWrapper>
    ),
    errorElement: <RouterErrorPage />,
  },
  {
    path: '/gst',
    element: (
      <OnboardingStepGuard>
        <LazyRouteWrapper>
          <LazyRoutes.Gst />
        </LazyRouteWrapper>
      </OnboardingStepGuard>
    ),
    errorElement: <RouterErrorPage />,
  },
  {
    path: '/bank',
    element: (
      <OnboardingStepGuard>
        <LazyRouteWrapper>
          <LazyRoutes.BankDetails />
        </LazyRouteWrapper>
      </OnboardingStepGuard>
    ),
    errorElement: <RouterErrorPage />,
  },
  {
    path: '/accverified',
    element: (
      <LazyRouteWrapper>
        <LazyRoutes.AccVerified />
      </LazyRouteWrapper>
    ),
    errorElement: <RouterErrorPage />,
  },
  {
    path: '/reject',
    element: (
      <LazyRouteWrapper>
        <LazyRoutes.RejectPage />
      </LazyRouteWrapper>
    ),
    errorElement: <RouterErrorPage />,
  },
  {
    path: '/beingverified',
    element: (
      <LazyRouteWrapper>
        <LazyRoutes.BeingVerifiedPage />
      </LazyRouteWrapper>
    ),
    errorElement: <RouterErrorPage />,
  },
  {
    path: '/under_review',
    element: (
      <OnboardingStepGuard>
        <LazyRouteWrapper>
          <LazyRoutes.UnderReviewPage />
        </LazyRouteWrapper>
      </OnboardingStepGuard>
    ),
    errorElement: <RouterErrorPage />,
  },
  {
    path: '/forward_penny',
    element: <Navigate to="/terms" replace />,
    errorElement: <RouterErrorPage />,
  },
  {
    path: '/reverse_penny',
    element: <Navigate to="/terms" replace />,
    errorElement: <RouterErrorPage />,
  },
  {
    path: '/terms',
    element: (
      <OnboardingStepGuard>
        <LazyRouteWrapper>
          <LazyRoutes.TermsCondition />
        </LazyRouteWrapper>
      </OnboardingStepGuard>
    ),
    errorElement: <RouterErrorPage />,
  },
  {
    path: '/renewal',
    element: (
      <LazyRouteWrapper>
        <LazyRoutes.PricingDetails />
      </LazyRouteWrapper>
    ),
    errorElement: <RouterErrorPage />,
  },
  {
    path: '/pricing',
    element: (
      <OnboardingStepGuard>
        <LazyRouteWrapper>
          <LazyRoutes.PricingDetails />
        </LazyRouteWrapper>
      </OnboardingStepGuard>
    ),
    errorElement: <RouterErrorPage />,
  },
  {
    path: '/payment/:order_id',
    element: (
      <LazyRouteWrapper>
        <LazyRoutes.AddPayment />
      </LazyRouteWrapper>
    ),
    errorElement: <RouterErrorPage />,
  },
  {
    path: '/forget',
    element: (
      <LazyRouteWrapper>
        <LazyRoutes.ForgetPage />
      </LazyRouteWrapper>
    ),
    errorElement: <RouterErrorPage />,
  },
  {
    path: '/forgetpasswordotp',
    element: (
      <LazyRouteWrapper>
        <LazyRoutes.ForgetPasswordOtpPage />
      </LazyRouteWrapper>
    ),
    errorElement: <RouterErrorPage />,
  },
  {
    path: '/resetpassword',
    element: (
      <LazyRouteWrapper>
        <LazyRoutes.ResetPassword />
      </LazyRouteWrapper>
    ),
    errorElement: <RouterErrorPage />,
  },
  // Catch-all route for any unmatched paths
  {
    path: '*',
    element: <RouterErrorPage />,
  },
];

const router = createBrowserRouter(dashboardRoutes, {
  basename: '/',
});

export default function DashboardRouter() {

  return (
    <React.Fragment>
      <RouterProvider router={router} />
    </React.Fragment>
  );
}
